import { ReactNode, useMemo, useState } from 'react'
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { Trans, useTranslation } from 'react-i18next'

import { CONTENT_LANGUAGE_CODES, getContentLanguageEndonym } from '../../utils/constants/language.constants'
import { useDarkMode } from '../../contexts/DarkModeContext'
import ContentLanguagePicker from '../ContentLanguagePicker'
import LocalizationFields from './LocalizationFields'
import { LocalizationDraft, localizationHasContent } from './useLocalizations'

const MAIN_TAB = '__main__'

interface LocalizationsEditorProps {
  /** Language of the spot's own base columns; never inferred from an experience. */
  baseLanguage?: string | null
  onBaseLanguageChange: (language: string) => void
  /** Whether the base content (the spot's own columns) has description or media: feeds the dot. */
  mainHasContent: boolean
  /** The base-content fields, owned by the hosting form: the first tab renders them as-is. */
  mainContent: ReactNode
  drafts: LocalizationDraft[]
  contentMediaType: number
  uploading: boolean
  onAddLanguage: (locale: string) => void
  onRemoveLanguage: (locale: string) => void
  onDescriptionChange: (locale: string, value: string) => void
  onMediaFileChange: (locale: string, file: File | null) => void
}

const languageLabel = (locale: string): string => getContentLanguageEndonym(locale)

/**
 * Per-language content editor (08.2): tabs, coverage dots, add/remove language.
 *
 * The contract is "given an agami, manage its localizations" — no reference to
 * Onde, none to "words": this is the genuinely reusable piece of the form work,
 * meant to graft into the standard form the day multilingual serves every venue.
 *
 * The MAIN tab is the hosting form's own fields (the spot's base columns, D6):
 * it arrives as a ReactNode and this component never writes it — which is what
 * keeps the monolingual agami the degenerate case. Its label comes only from
 * the spot's base_language; an experience preference never relabels content.
 * A legacy spot with no declared base stays "Main content".
 *
 * The title is NOT localizable (R2, D6) and stays outside these tabs entirely.
 */
const LocalizationsEditor: React.FC<LocalizationsEditorProps> = ({
  baseLanguage,
  onBaseLanguageChange,
  mainHasContent,
  mainContent,
  drafts,
  contentMediaType,
  uploading,
  onAddLanguage,
  onRemoveLanguage,
  onDescriptionChange,
  onMediaFileChange,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()

  const [activeTab, setActiveTab] = useState<string>(MAIN_TAB)
  const [localeToRemove, setLocaleToRemove] = useState<string | null>(null)

  const mainLabel = baseLanguage ? languageLabel(baseLanguage) : t('localizations.mainTab')

  // Languages still addable: the canonical content list minus the spot base
  // and the localizations already present.
  const addableLanguageCodes = useMemo(
    () =>
      CONTENT_LANGUAGE_CODES.filter(
        (code) => code !== baseLanguage && !drafts.some((draft) => draft.locale === code)
      ),
    [baseLanguage, drafts]
  )

  const selectableBaseLanguageCodes = useMemo(
    () => CONTENT_LANGUAGE_CODES.filter((code) => !drafts.some((draft) => draft.locale === code)),
    [drafts]
  )

  const activeDraft = drafts.find((d) => d.locale === activeTab)

  const tabClass = (selected: boolean) =>
    `px-3 py-1 rounded text-sm font-bold cursor-pointer border ${
      selected
        ? 'border-primary text-primary bg-white-yellow dark:bg-accent-blue-600 dark:text-white'
        : 'border-gray-300 text-gray-600 dark:text-base-gray-500'
    }`

  const coverageDot = (covered: boolean) => (
    <span
      className={covered ? 'text-green-700 dark:text-green-300' : 'text-gray-400'}
      data-testclass={covered ? 'localization-covered' : 'localization-empty'}
    >
      {covered ? '●' : '○'}
    </span>
  )

  return (
    <div className="flex flex-col gap-4" data-testid="localizations-editor">
      <div className="flex items-center gap-2 flex-wrap" data-testid="localization-tabs">
        <button
          type="button"
          className={tabClass(activeTab === MAIN_TAB)}
          onClick={() => setActiveTab(MAIN_TAB)}
          data-testid="localization-tab-main"
        >
          {mainLabel} {coverageDot(mainHasContent)}
        </button>

        {drafts.map((draft) => (
          <button
            key={draft.locale}
            type="button"
            className={tabClass(activeTab === draft.locale)}
            onClick={() => setActiveTab(draft.locale)}
            data-testclass="localization-tab"
            data-testid={`localization-tab-${draft.locale}`}
          >
            {languageLabel(draft.locale)} {coverageDot(localizationHasContent(draft))}
          </button>
        ))}

        {addableLanguageCodes.length > 0 && (
          <ContentLanguagePicker
            languageCodes={addableLanguageCodes}
            onSelect={(code) => {
              onAddLanguage(code)
              setActiveTab(code)
            }}
            disabled={uploading || !baseLanguage}
            buttonLabel={t('localizations.addLanguage')}
            buttonTestId="localization-add-language-btn"
            optionTestIdPrefix="localization-add"
            buttonAriaDescribedBy={!baseLanguage ? 'base-language-required-hint' : undefined}
            disabledTooltip={!baseLanguage ? t('localizations.baseLanguageRequired') : undefined}
          />
        )}
      </div>

      {/* The main language is NOT removable: it is the spot's base content, and
          removing it would empty the agami. No remove control exists on it. */}
      {activeTab === MAIN_TAB ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-start gap-3 rounded-lg bg-white p-4 shadow-sm dark:bg-primary">
            <Typography className="font-bold text-primary font-barlow">
              {t('localizations.baseLanguage')}
            </Typography>
            <ContentLanguagePicker
              languageCodes={selectableBaseLanguageCodes}
              onSelect={onBaseLanguageChange}
              disabled={uploading}
              buttonLabel={
                baseLanguage ? languageLabel(baseLanguage) : t('localizations.declareBaseLanguage')
              }
              buttonAriaLabel={
                baseLanguage ? t('localizations.changeBaseLanguage') : t('localizations.declareBaseLanguage')
              }
              buttonTestId="base-language-picker-btn"
              optionTestIdPrefix="base-language-option"
              showAddIcon={false}
              containerClassName="w-full max-w-sm"
              buttonClassName="w-full"
            />
            {!baseLanguage && (
              <Typography
                id="base-language-required-hint"
                variant="small"
                className="text-gray-600 dark:text-base-gray-500"
              >
                {t('localizations.baseLanguageRequired')}
              </Typography>
            )}
          </div>
          {mainContent}
        </div>
      ) : (
        activeDraft && (
          <LocalizationFields
            key={activeDraft.locale}
            draft={activeDraft}
            languageLabel={languageLabel(activeDraft.locale)}
            contentMediaType={contentMediaType}
            uploading={uploading}
            onDescriptionChange={(value) => onDescriptionChange(activeDraft.locale, value)}
            onMediaFileChange={(file) => onMediaFileChange(activeDraft.locale, file)}
            onRemove={() => setLocaleToRemove(activeDraft.locale)}
          />
        )
      )}

      {/* The one destructive operation of the epic: a row and a file, no undo.
          The confirm names the language — "Remove the French content?" is a
          different question from "Confirm?". */}
      <Dialog
        size="xs"
        open={localeToRemove !== null}
        handler={() => setLocaleToRemove(null)}
        data-testid="localization-remove-dialog"
      >
        <DialogHeader>{t('localizations.removeDialog.title')}</DialogHeader>
        <DialogBody>
          <Typography>
            <Trans
              i18nKey="localizations.removeDialog.warning"
              values={{ language: localeToRemove ? languageLabel(localeToRemove) : '' }}
              components={{ strong: <strong className="font-extrabold" /> }}
            />
          </Typography>
        </DialogBody>
        <DialogFooter className="justify-between gap-2">
          <Button
            size="sm"
            color={darkMode ? 'white' : ('primary' as colors)}
            variant="outlined"
            onClick={() => setLocaleToRemove(null)}
          >
            {t('localizations.removeDialog.cancel')}
          </Button>
          <Button
            size="sm"
            color="red"
            onClick={() => {
              if (localeToRemove) {
                onRemoveLanguage(localeToRemove)
                setActiveTab(MAIN_TAB)
              }
              setLocaleToRemove(null)
            }}
            data-testid="localization-remove-confirm-btn"
          >
            {t('localizations.removeDialog.confirm')}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  )
}

export default LocalizationsEditor
